import { useEffect, useRef } from 'react';

/** Runs `onEscape` when Escape is pressed with nothing else claiming it: not
 *  while a dialog is open, not inside a menu, list, text field or the header search (each
 *  of which closes itself on Escape), and not once something has already
 *  handled the key. */
export function useEscape(enabled: boolean, onEscape: () => void): void {
  const ref = useRef(onEscape);
  ref.current = onEscape;
  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('[role="combobox"],[role="listbox"],[role="menu"],[role="dialog"]')) {
        return;
      }
      // Typing in a field: Escape must not throw the form away.
      if (target?.closest('textarea,select,input:not([type="checkbox"]):not([type="radio"])')) {
        return;
      }
      ref.current();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
